import { supabase } from './supabase';
import { getStaffTenantHeader } from './tenant';

// Cache by user so switching accounts in the same browser cannot reuse another HOA.
const cachedHoaIds = new Map();

async function getHoaId(userId) {
  if (cachedHoaIds.has(userId)) return cachedHoaIds.get(userId);
  const { data, error } = await supabase
    .from('user_profiles')
    .select('hoa_id')
    .eq('id', userId)
    .single();
  if (error) throw error;
  const hoaId = data?.hoa_id || null;
  if (hoaId) cachedHoaIds.set(userId, hoaId);
  return hoaId;
}

/**
 * Fire-and-forget activity event logger.
 * Does not interrupt the caller. Returns false when recording fails.
 *
 * When an admin is impersonating another user (stored in localStorage),
 * events are logged as the target user with _admin_id and _impersonated metadata.
 *
 * @param {string} eventType - One of: login, section_view, document_view, document_upload, rag_query
 * @param {Object} metadata  - Arbitrary JSON payload stored alongside the event
 */
export async function logEvent(eventType, metadata = {}, { respectImpersonation = true } = {}) {
  // Atrionos staff working inside another association: their clicks must not
  // count as that association's engagement (and RLS would refuse the home hoa_id).
  if (getStaffTenantHeader()) return false;
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) return false;

    const impersonationRaw = respectImpersonation
      ? localStorage.getItem('impersonating')
      : null;
    const impersonating = impersonationRaw ? JSON.parse(impersonationRaw) : null;

    const effectiveUserId = impersonating?.id ?? user.id;
    const enhancedMetadata = impersonating
      ? { ...metadata, _admin_id: user.id, _impersonated: true }
      : metadata;

    const hoaId = await getHoaId(user.id);
    if (!hoaId) return false;

    const { error: insertError } = await supabase.from('activity_events').insert({
      hoa_id: hoaId,
      user_id: effectiveUserId,
      event_type: eventType,
      metadata: enhancedMetadata,
      occurred_at: new Date().toISOString(),
    });
    if (insertError) {
      console.warn('[Activity] Event could not be recorded:', insertError.message);
      return false;
    }
    return true;
  } catch (error) {
    console.warn('[Activity] Event could not be recorded:', error?.message || error);
    return false;
  }
}
